import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Import locale files
import en from './locales/en.json';
import es from './locales/es.json';
import he from './locales/he.json';

export const locales = {
  en: 'English',
  es: 'Español',
  he: 'עברית',
} as const;

export type Locale = keyof typeof locales;
export const defaultLocale: Locale = 'en';

// Persisted in BOTH localStorage and a cookie. The cookie lets the server read
// the chosen locale during SSR so the first paint is already in the right
// language/direction (no English -> Hebrew flash on load).
export const LOCALE_COOKIE = 'prizo-locale';

// Parse the locale from a raw Cookie header (server-safe — no window access).
export const getLocaleFromCookie = (cookieHeader?: string | null): Locale => {
  if (!cookieHeader) return defaultLocale;
  const entry = cookieHeader
    .split(';')
    .map((s) => s.trim())
    .find((s) => s.startsWith(`${LOCALE_COOKIE}=`));
  if (entry) {
    const value = decodeURIComponent(entry.slice(LOCALE_COOKIE.length + 1));
    if (value in locales) return value as Locale;
  }
  return defaultLocale;
};

// Create the translations object
export const translations = {
  en,
  es,
  he,
} as const;

// Type for the i18n context
interface I18nContextType {
  locale: Locale;
  changeLocale: (newLocale: Locale) => void;
  t: (key: string, params?: Record<string, string>) => string;
}

// Type for the games language context
interface GamesLanguageContextType {
  gamesLanguage: Locale;
  changeGamesLanguage: (newLanguage: Locale) => void;
}

// Create the contexts
const I18nContext = createContext<I18nContextType | undefined>(undefined);
const GamesLanguageContext = createContext<GamesLanguageContextType | undefined>(undefined);

// Custom hook to use i18n
export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    // Fallback gracefully to default locale when provider is not present
    const locale = defaultLocale;
    const t = (key: string, params?: Record<string, string>) => translate(key, locale, params);
    const noop = () => {};
    return { locale, changeLocale: noop, t } as I18nContextType;
  }
  return context;
};

// Custom hook to use games language
export const useGamesLanguageContext = () => {
  const context = useContext(GamesLanguageContext);
  if (!context) {
    throw new Error('useGamesLanguageContext must be used within a GamesLanguageProvider');
  }
  return context;
};

// Custom hook to get games language
export const useGamesLanguage = (clientData?: any) => {
  const { gamesLanguage } = useGamesLanguageContext();
  
  // If no client data, use the games language from context
  if (!clientData) {
    return gamesLanguage;
  }
  
  // Prioritize database value (clientData.gamesLanguage) over context/localStorage
  const databaseGamesLanguage = clientData.gamesLanguage;
  
  // If database has a valid games language setting, use it
  if (databaseGamesLanguage && Object.keys(locales).includes(databaseGamesLanguage)) {
    return databaseGamesLanguage;
  }
  
  // Fallback to context (localStorage) if no database setting
  return gamesLanguage;
};

// Function to get games language locale
export const getGamesLanguageLocale = (clientData?: any, adminLocale?: Locale): Locale => {
  const fallbackLocale = adminLocale || defaultLocale;
  
  if (!clientData) {
    return fallbackLocale;
  }
  
  // Prioritize database value (clientData.gamesLanguage)
  const databaseGamesLanguage = clientData.gamesLanguage;
  
  // If database has a valid games language setting, use it
  if (databaseGamesLanguage && Object.keys(locales).includes(databaseGamesLanguage)) {
    return databaseGamesLanguage;
  }
  
  // Fallback to admin locale or default
  return fallbackLocale;
};

// Translation function
const translate = (key: string, locale: Locale, params?: Record<string, string>): string => {
  const keys = key.split('.');
  let value: any = translations[locale];
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // Fallback to English if translation not found
      value = translations.en;
      for (const fallbackKey of keys) {
        if (value && typeof value === 'object' && fallbackKey in value) {
          value = value[fallbackKey];
        } else {
          return key; // Return the key if no translation found
        }
      }
      break;
    }
  }
  
  if (typeof value === 'string') {
    // Replace parameters like {{name}} with actual values
    if (params) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, paramName) => {
        return params[paramName] || match;
      });
    }
    return value;
  }
  
  return key;
};

// Provider component
interface I18nProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
}

export const I18nProvider = ({ children, initialLocale = defaultLocale }: I18nProviderProps) => {
  const [locale, setLocale] = useState<Locale>(initialLocale);

  // One-time migration for users who chose a locale before the cookie existed
  // (it was only stored in localStorage). Mirror it into the cookie so the next
  // SSR render already knows the language and there's no flash.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const hasCookie = document.cookie.split(';').some((s) => s.trim().startsWith(`${LOCALE_COOKIE}=`));
    if (hasCookie) return;
    const saved = localStorage.getItem('prizo-locale') as Locale | null;
    if (saved && saved in locales) {
      document.cookie = `${LOCALE_COOKIE}=${saved}; path=/; max-age=31536000; SameSite=Lax`;
    }
  }, []);

  const changeLocale = (newLocale: Locale) => {
    setLocale(newLocale);
    if (typeof window !== 'undefined') {
      // localStorage + cookie give instant client persistence and let SSR read
      // the choice on the next load (no flash).
      localStorage.setItem('prizo-locale', newLocale);
      document.cookie = `${LOCALE_COOKIE}=${newLocale}; path=/; max-age=31536000; SameSite=Lax`;
      // Also persist to the user's account so it follows them across devices.
      // Fire-and-forget: returns 401 for logged-out visitors, which we ignore.
      fetch('/api/locale', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locale: newLocale }),
      }).catch(() => {});
    }
  };

  const t = (key: string, params?: Record<string, string>) => {
    return translate(key, locale, params);
  };

  const value: I18nContextType = {
    locale,
    changeLocale,
    t,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

// Games Language Provider component
interface GamesLanguageProviderProps {
  children: ReactNode;
  initialGamesLanguage?: Locale;
}

export const GamesLanguageProvider = ({ children, initialGamesLanguage = defaultLocale }: GamesLanguageProviderProps) => {
  const [gamesLanguage, setGamesLanguage] = useState<Locale>(initialGamesLanguage);

  const changeGamesLanguage = (newLanguage: Locale) => {
    setGamesLanguage(newLanguage);
    // Save to localStorage for persistence
    if (typeof window !== 'undefined') {
      localStorage.setItem('prizo-games-language', newLanguage);
    }
  };

  const value: GamesLanguageContextType = {
    gamesLanguage,
    changeGamesLanguage,
  };

  return (
    <GamesLanguageContext.Provider value={value}>
      {children}
    </GamesLanguageContext.Provider>
  );
};

// Initialize locale from localStorage on client side
export const initializeLocale = (): Locale => {
  if (typeof window !== 'undefined') {
    const savedLocale = localStorage.getItem('prizo-locale') as Locale;
    if (savedLocale && savedLocale in locales) {
      return savedLocale;
    }
  }
  return defaultLocale;
};

// Initialize games language from localStorage on client side
export const initializeGamesLanguage = (): Locale => {
  if (typeof window !== 'undefined') {
    const savedGamesLanguage = localStorage.getItem('prizo-games-language') as Locale;
    if (savedGamesLanguage && savedGamesLanguage in locales) {
      return savedGamesLanguage;
    }
  }
  return defaultLocale;
};

// ── Games-scoped i18n ────────────────────────────────────────────────────────
// The customer-facing games must render in the merchant's selected Games
// Language (clientData.gamesLanguage), NOT the admin locale (which a shopper
// never sets). useGamesI18n binds `t` to that language and exposes the writing
// direction, so the same `t(...)` calls render Hebrew right-to-left when the
// merchant picks Hebrew.

// Locales that read right-to-left.
export const RTL_LOCALES: Locale[] = ['he'];

/**
 * The merchant's global Order Requirements (min order amount + required
 * collections), surfaced to the customer-facing reward modal as "Valid on ..."
 * text. Sourced from clientData.gameSchedule so it reflects the live settings
 * for every coupon, independent of per-reward data.
 */
export interface CouponConditions {
  minAmount?: number;
  minCurrency?: string;
  collectionNames?: string[];
}

export interface GamesI18n {
  t: (key: string, params?: Record<string, string>) => string;
  locale: Locale;
  isRTL: boolean;
  dir: 'rtl' | 'ltr';
  conditions?: CouponConditions;
}

// Derive the displayable coupon conditions from a client's global gameSchedule.
// orderRequirement / productRequirement are null when their toggle is off
// (see CustomizeComponents), so optional chaining naturally skips them.
const deriveCouponConditions = (clientData?: any): CouponConditions | undefined => {
  const gs = clientData?.gameSchedule;
  if (!gs) return undefined;

  const conditions: CouponConditions = {};

  const minAmount = gs.orderRequirement?.minAmount;
  if (typeof minAmount === 'number' && minAmount > 0) {
    conditions.minAmount = minAmount;
    conditions.minCurrency = 'USD'; // The Order Requirements UI is labeled USD.
  }

  const pr = gs.productRequirement;
  if (pr && pr.type === 'collections') {
    const names = Array.isArray(pr.collections)
      ? pr.collections.map((c: any) => c?.title || c?.name).filter(Boolean)
      : [];
    if (names.length > 0) conditions.collectionNames = names;
  }

  return conditions.minAmount || conditions.collectionNames ? conditions : undefined;
};

const buildGamesI18n = (locale: Locale, clientData?: any): GamesI18n => {
  const isRTL = RTL_LOCALES.includes(locale);
  return {
    locale,
    isRTL,
    dir: isRTL ? 'rtl' : 'ltr',
    t: (key: string, params?: Record<string, string>) => translate(key, locale, params),
    conditions: deriveCouponConditions(clientData),
  };
};

const GamesI18nContext = createContext<GamesI18n | undefined>(undefined);

/**
 * Resolve the games-scoped translation helpers ({ t, locale, isRTL, dir }).
 * - Pass `clientData` (at the top of a game component) to bind directly to the
 *   merchant's gamesLanguage.
 * - Call with no args (nested components such as RewardModal) to read the
 *   nearest GamesI18nProvider; falls back to English when none is present.
 */
export const useGamesI18n = (clientData?: any): GamesI18n => {
  const ctx = useContext(GamesI18nContext);
  if (clientData !== undefined) {
    return buildGamesI18n(getGamesLanguageLocale(clientData), clientData);
  }
  return ctx ?? buildGamesI18n(defaultLocale);
};

interface GamesI18nProviderProps {
  clientData?: any;
  children: ReactNode;
}

/**
 * Makes the games-scoped translation + direction available to a whole game
 * subtree (game board + the shared RewardModal), so nested components can call
 * `useGamesI18n()` without being handed `clientData`. Context only — each
 * consumer applies `dir` to its own root element to avoid layout surprises.
 */
export const GamesI18nProvider = ({ clientData, children }: GamesI18nProviderProps) => {
  const value = buildGamesI18n(getGamesLanguageLocale(clientData), clientData);
  return <GamesI18nContext.Provider value={value}>{children}</GamesI18nContext.Provider>;
};
